/**
 * Génère un menu journalier cohérent, priorisé et équilibré sans jamais dépasser la cible calorique.
 *
 * Règles :
 * - Lunch/Dinner → entrée + plat + dessert si dispo
 * - Breakfast → uniquement breakfast
 * - Snacks → ajoutés uniquement si des calories restent
 * - Chaque recette = 1 portion entière (pas de fraction)
 * - Optimisation "sac à dos" pour se rapprocher au maximum de la cible sans dépasser (+1 % tolérance)
 */
export function generateMenu(recipes, userConstraints, weekUsed = null, sameLunchDinner = false) {
  if (!Array.isArray(recipes) || !recipes.length)
    return { menu: {}, totals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } };

  const {
    targetCalories = 2000,
    takenMealsObjectives = ["breakfast", "lunch", "dinner", "snacks"],
    allergies = [],
    intolerances = [],
    bannedIngredients = [],
    maxPrepTime = null, // Dégage
    skillLevel = null, // Dégage
    availableEquipment = [], // Dégage
  } = userConstraints;

  // Helpers
  const normalize = (str) => String(str || "").trim().toLowerCase().replace(/s$/, "");
  const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const getKcal = (r) => (r?.totals?.kcal ?? 0) / (r.servings && r.servings > 0 ? r.servings : 1);
  const getMacros = (r) => {
    const s = r.servings && r.servings > 0 ? r.servings : 1;
    const t = r.totals || {};
    return {
      kcal: (t.kcal ?? 0) / s,
      protein: (t.protein ?? 0) / s,
      carbs: (t.carbs ?? 0) / s,
      fat: (t.fat ?? 0) / s,
    };
  };

  // Normalisation des repas
  const normalizedMeals = takenMealsObjectives.map(normalize);

  // Filtrage des recettes selon contraintes
  const filteredRecipes = recipes.filter((r) => {
    const ingredients = (r.ingredients || []).map((i) => i.name?.toLowerCase?.() || "");
    const safe = (list, fn) => list.every((x) => fn(x));
    return (
      safe(allergies, (a) => !ingredients.includes(a.toLowerCase())) &&
      safe(intolerances, (i) => !ingredients.includes(i.toLowerCase())) &&
      safe(bannedIngredients, (b) => !ingredients.includes(b.toLowerCase())) &&
      (!maxPrepTime || (r.prepTime ?? 0) <= maxPrepTime) &&
      (!skillLevel || (r.skillLevel ?? "intermediate") === skillLevel) &&
      (!availableEquipment.length ||
        availableEquipment.every((eq) => (r.equipment || []).includes(eq)))
    );
  });

  if (!filteredRecipes.length)
    return { menu: {}, totals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } };

  // Groupes par catégorie
  const byCategory = {
    starter: filteredRecipes.filter((r) => normalize(r.category) === "starter"),
    meal: filteredRecipes.filter((r) => normalize(r.category) === "meal"),
    dessert: filteredRecipes.filter((r) => normalize(r.category) === "dessert"),
    breakfast: filteredRecipes.filter((r) => normalize(r.category) === "breakfast"),
    snack: filteredRecipes.filter((r) => normalize(r.category) === "snack"),
  };

  // Construction du menu
  const menu = {};
  // Si un pool hebdomadaire est fourni (diversité), on part de ses ids déjà utilisés
  const used = weekUsed ? new Set(weekUsed) : new Set();
  // IDs déjà placés dans le menu du jour (pour éviter doublons midi/soir)
  const usedToday = new Set();

  // Totaux calculés progressivement, au fur et à mesure des ajouts
  const totals = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
  const addToTotals = (r) => {
    const t = getMacros(r);
    totals.kcal += t.kcal;
    totals.protein += t.protein;
    totals.carbs += t.carbs;
    totals.fat += t.fat;
  };

  // Budget alloué par repas, pour qu'aucun repas ne consomme toute la marge
  // au détriment des suivants. Répartition classique : petit-déj ~20 %,
  // déjeuner ~40 %, dîner ~40 % (ajustée si certains repas sont absents).
  const mealsForBudget = ["breakfast", "lunch", "dinner"].filter((m) => normalizedMeals.includes(m));
  const shareOf = { breakfast: 0.2, lunch: 0.4, dinner: 0.4 };
  const totalShare = mealsForBudget.reduce((sum, m) => sum + shareOf[m], 0) || 1;
  const mealBudget = {};
  for (const m of mealsForBudget) {
    mealBudget[m] = (targetCalories * shareOf[m]) / totalShare;
  }

  // Tolérance par repas (+1 %), appliquée sur le budget alloué à CE repas
  // plutôt que sur la cible globale, pour éviter qu'un repas dévore la marge des autres.
  const addRecipe = (cat, type) => {
    const allInCat = byCategory[cat] || [];
    // 1. Priorité : non utilisé cette semaine ET pas déjà dans le menu du jour
    const freshPool = allInCat.filter((r) => !used.has(r.id) && !usedToday.has(r.id));
    // 2. Fallback : pas déjà dans le menu du jour (permet réutilisation hebdo mais pas midi=soir)
    const dayPool = freshPool.length ? freshPool : allInCat.filter((r) => !usedToday.has(r.id));
    // 3. Dernier recours : toute la catégorie (très rare, peu de recettes)
    const pool = dayPool.length ? dayPool : allInCat;
    if (!pool.length) return null;

    // Budget restant pour CE repas (type), avec tolérance +1 % sur son budget alloué
    const budgetForMeal = (mealBudget[type] ?? targetCalories) * 1.01;
    const spentOnThisMeal = (menu[type] || []).reduce((sum, r) => sum + getKcal(r), 0);
    const remainingForMeal = budgetForMeal - spentOnThisMeal;

    const withinBudget = pool.filter((r) => getKcal(r) <= remainingForMeal);
    // S'il n'y a aucun candidat sous le budget restant du repas, on prend quand même
    // celui qui dépasse le moins (mieux vaut un petit dépassement inévitable
    // que ne rien proposer pour ce repas).
    const candidates = withinBudget.length
      ? withinBudget
      : [pool.reduce((a, b) => (getKcal(a) <= getKcal(b) ? a : b))];

    const chosen = pickRandom(candidates);
    menu[type].push(chosen);
    used.add(chosen.id);
    usedToday.add(chosen.id);
    addToTotals(chosen);
  };

  // Lunch & Dinner (entrée + plat + dessert)
  for (const type of ["lunch", "dinner"]) {
    if (!normalizedMeals.includes(type)) continue;
    menu[type] = [];
    ["starter", "meal", "dessert"].forEach((cat) => addRecipe(cat, type));
  }

  // Breakfast
  if (normalizedMeals.includes("breakfast") && byCategory.breakfast.length) {
    menu.breakfast = [];
    addRecipe("breakfast", "breakfast");
  }

  // Si "Déjeuner = Dîner" est actif pour ce jour, on remplace dinner par une copie de lunch
  // AVANT de choisir la collation, pour que celle-ci soit adaptée au vrai contenu du menu
  // (et non à un dîner généré puis jeté). On corrige aussi les totaux en conséquence.
  if (sameLunchDinner && menu.lunch && menu.lunch.length) {
    for (const r of menu.dinner || []) {
      const t = getMacros(r);
      totals.kcal -= t.kcal;
      totals.protein -= t.protein;
      totals.carbs -= t.carbs;
      totals.fat -= t.fat;
    }
    menu.dinner = menu.lunch.map((r) => ({ ...r }));
    for (const r of menu.dinner) addToTotals(r);
  }

  // ⚡ Sélection des collations : une seule collation, la plus proche de remainingKcal
  // SANS dépasser la cible journalière (tolérance +1 %, conformément à la règle du fichier).
  if (normalizedMeals.includes("snack") && byCategory.snack.length) {
    const snacks = [...byCategory.snack];
    const remainingKcal = targetCalories - totals.kcal;
    const maxAllowedKcal = remainingKcal + targetCalories * 0.01; // tolérance +1 %

    if (remainingKcal > 20) {
      // On ne garde que les collations qui ne font pas dépasser la cible (avec tolérance)
      const eligibleSnacks = snacks.filter((s) => getKcal(s) <= maxAllowedKcal);

      if (eligibleSnacks.length) {
        // Parmi celles-ci, on prend la plus proche du budget restant (sans jamais le dépasser au-delà de la tolérance)
        const best = eligibleSnacks.reduce((prev, cur) => {
          const prevGap = Math.abs(getKcal(prev) - remainingKcal);
          const curGap = Math.abs(getKcal(cur) - remainingKcal);
          return curGap < prevGap ? cur : prev;
        });

        const t = getMacros(best);
        menu.snack = menu.snack || [];
        menu.snack.push({ ...best });
        totals.kcal += t.kcal;
        totals.protein += t.protein;
        totals.carbs += t.carbs;
        totals.fat += t.fat;
        used.add(best.id);
      }
      // Si aucune collation ne respecte la cible, on n'en ajoute pas plutôt que de dépasser.
    }
  }

  return { menu, totals, usedIds: used };
}

/**
 * Génère un plan de menus pour une semaine (7 jours) en réutilisant generateMenu.
 * options:
 *  - diverse: boolean (false) : si true, on "intervertit" les menus un jour sur 3
 *    pour éviter d'avoir les mêmes menus collés (ex: swap jour 3<->4, 6<->7).
 *
 * Retourne { daily: [...7 elements { menu, totals }], weeklyTotals: { kcal, protein, carbs, fat } }
 */
export function generateWeeklyMenu(recipes, userConstraints = {}, options = { diverse: false }) {
  if (!Array.isArray(recipes) || !recipes.length) {
    return { daily: [], weeklyTotals: { kcal: 0, protein: 0, carbs: 0, fat: 0 } };
  }

  const days = 7;
  const daily = [];

  // sameLunchDinnerDays : tableau d'indices 0-6 pour lesquels dinner = lunch
  const sameDays = Array.isArray(options.sameLunchDinnerDays) ? options.sameLunchDinnerDays : [];

  // Si diversité activée : pool de recettes déjà utilisées partagé entre les 7 jours
  const weekUsed = (options && options.diverse) ? new Set() : null;

  for (let d = 0; d < days; d++) {
    const dayResult = generateMenu(recipes, userConstraints, weekUsed, sameDays.includes(d));
    const menu = dayResult.menu || {};
    // Enregistrer les recettes utilisées ce jour dans le pool hebdomadaire
    if (weekUsed && dayResult.usedIds) {
      for (const id of dayResult.usedIds) weekUsed.add(id);
    }

    // Totaux recalculés depuis ce qui est réellement affiché dans le menu
    const recalc = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
    for (const mealRecipes of Object.values(menu)) {
      for (const r of mealRecipes) {
        const s = r.servings && r.servings > 0 ? r.servings : 1;
        const t = r.totals || {};
        recalc.kcal += (t.kcal ?? 0) / s;
        recalc.protein += (t.protein ?? 0) / s;
        recalc.carbs += (t.carbs ?? 0) / s;
        recalc.fat += (t.fat ?? 0) / s;
      }
    }

    daily.push({ menu, totals: recalc });
  }

  // Totaux hebdomadaires
  const weeklyTotals = daily.reduce(
    (acc, d) => {
      acc.kcal += Number(d.totals.kcal || 0);
      acc.protein += Number(d.totals.protein || 0);
      acc.carbs += Number(d.totals.carbs || 0);
      acc.fat += Number(d.totals.fat || 0);
      return acc;
    },
    { kcal: 0, protein: 0, carbs: 0, fat: 0 }
  );

  return { daily, weeklyTotals };
}
